import { useEffect } from 'react';

import * as Sentry from '@sentry/browser';

interface DocusignSessionEndEventContext {
  returnUrl: string;
  sessionEndType:
    | 'cancel'
    | 'decline'
    | 'exception'
    | 'fax_pending'
    | 'session_timeout'
    | 'ttl_expired'
    | 'viewing_complete'
    | 'signing_complete';
  type: 'sessionEnd';
}

export type DocusignSessionEndEventProps = DocusignSessionEndEventContext;
interface DocusignContext {
  docusignClientId: string;
  docusignSignatureURL: string;
  onReady?: () => void;
  onSessionEnd?: (event: DocusignSessionEndEventProps) => void;
}

export type DocusignProps = DocusignContext;

const injectExternalScript = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      resolve();
    };

    script.onerror = () => {
      reject(new Error(`Failed to load script: ${url}`));
    };

    document.head.appendChild(script);
  });
};

const Docusign: React.FC<React.PropsWithChildren<DocusignProps>> = ({
  docusignClientId,
  docusignSignatureURL,
  onReady,
  onSessionEnd,
}) => {
  const loadDocusign = async () => {
    if (!docusignClientId || !docusignSignatureURL) {
      throw new Error('Docusign Client ID and Signature URL are required');
    }

    const signingConfiguration = {
      url: docusignSignatureURL,
      displayFormat: 'focused',
    };

    try {
      // @ts-ignore: Errors with 'Property 'DocuSign' does not exist on type 'Window & typeof globalThis''. We should find existing typing or write our own.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      const docusign = await window.DocuSign.loadDocuSign(docusignClientId);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      const signing = docusign.signing(signingConfiguration);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      signing.on('ready', onReady);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      signing.on('sessionEnd', onSessionEnd);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      signing.mount('#signing-ceremony');
    } catch (error) {
      // Any configuration or API limits will be caught here
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    injectExternalScript('https://js-d.docusign.com/bundle.js')
      .then(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        loadDocusign();
      })
      .catch(error => {
        Sentry.captureException(error);
      });
  }, []);

  return (
    <div className="u-border" id="signing-ceremony" data-testid="signing-ceremony">
      <br />
    </div>
  );
};

export default Docusign;
